/**
 * Marks drawn by hand — the strokes that divide a client's column into stages,
 * the rule under a client's name, and the line down the side that separates one
 * client from the next.
 *
 * Every mark is generated from a seed string, never from Math.random, so a line
 * keeps the same wobble across every render, every reload and every machine.
 * A line that re-wobbles on each frame reads as a glitch, not as ink.
 *
 * Strokes are *filled* outlines rather than stroked lines. A stroke has one
 * width along its whole length; a marker is fat in the middle and tapers to
 * nothing at the ends, and that taper is most of what makes it read as ink.
 */

/** Stable 32-bit hash of a string. Also used for a sticky's tilt. */
export function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** mulberry32 — small, fast, and identical everywhere for a given seed. */
function random(seed: string): () => number {
  let state = hashString(seed);
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A finished mark: the path, plus the box it occupies. The box is given in the
 * mark's own coordinates — for a horizontal stroke y = 0 is the baseline, so
 * `y` is negative and the caller offsets by it. Callers must render at exactly
 * this size: scaling a viewBox to a different aspect stretches the ink and the
 * taper stops looking like a marker.
 */
export type Mark = { d: string; viewBox: string; x: number; y: number; width: number; height: number };

/** Fat in the middle, nothing at the tips. The exponent controls how fast it swells. */
function taper(t: number, thickness: number): number {
  return (thickness / 2) * Math.sin(Math.PI * t) ** 0.55;
}

const round = (n: number) => Math.round(n * 10) / 10;

/**
 * A sweeping horizontal stroke — the line between two stage bands, and, thinner,
 * the rule under a client's name.
 *
 * The vertical rules already read as separate lines because each one wanders on
 * its own; the horizontals did not, because only their ends and their bow moved
 * and everything else — thickness, direction of curve, slope, wobble — was the
 * same on every one. Everything is drawn from the seed now:
 *
 *   • which way it bows, and how far. Some arch up, some sag.
 *   • where the peak of the sweep falls along its length.
 *   • the slope from one end to the other, so a line is rarely level.
 *   • two wobbles of different frequency, with their own strengths and phases.
 *   • how thick the pen ran, ±25% of the caller's nominal thickness.
 *   • where it starts and stops, which end tapers hardest.
 *
 * The parameters are what the *caller* means — a separator asks for a thicker,
 * larger sweep than a name rule — and the seed decides everything within that.
 * The bounding box is measured from the points actually drawn rather than
 * predicted from the parameters, so a line that sags is not clipped.
 */
export function archPath(
  width: number,
  seed: string,
  { thickness = 9, rise = 9, samples = 34 }: { thickness?: number; rise?: number; samples?: number } = {},
): Mark {
  const rand = random(seed);

  // Where the stroke starts and stops, as a fraction of the width. Drawn by
  // hand, neither end lands on the edge, and the two ends do not match.
  const from = width * (0.015 + rand() * 0.06);
  const to = width * (0.985 - rand() * 0.07);
  const span = to - from;

  // How fat the pen ran on this particular line. A marker held flatter lays
  // down more ink, and nobody holds it the same way twice.
  const weight = thickness * (0.75 + rand() * 0.5);

  // The sweep. A negative bow arches up, a positive one sags — one in three
  // does. Both are lines someone drew across a board without a straightedge.
  const bow = rise * (0.35 + rand() * 1.15) * (rand() < 0.32 ? -1 : 1);
  // Where along the line the sweep peaks. Away from the middle it reads as a
  // line drawn faster at one end than the other.
  const peak = 0.3 + rand() * 0.4;
  // End-to-end slope: the far end sits above or below the near one.
  const slope = (rand() - 0.5) * rise * 2.2;

  // Two wobbles: a slow one that bends the whole line, a quick one that is the
  // texture of a hand that is not quite steady.
  const slowAmp = weight * (0.1 + rand() * 0.5);
  const slowFreq = 1.3 + rand() * 2.4;
  const slowPhase = rand() * Math.PI * 2;
  const fastAmp = weight * (0.04 + rand() * 0.14);
  const fastFreq = 6 + rand() * 8;
  const fastPhase = rand() * Math.PI * 2;

  // Which end the pen lifted from faster. 1 is symmetric; away from it, one
  // tip is a long thin trail and the other is blunter.
  const lift = 0.7 + rand() * 0.7;

  const top: string[] = [];
  const bottom: string[] = [];
  let lowest = Infinity;
  let highest = -Infinity;
  for (let i = 0; i <= samples; i += 1) {
    const t = i / samples;
    const x = from + span * t;
    // sin(pi * t^k) peaks at t = 0.5^(1/k), so k is chosen from where the
    // sweep is wanted rather than being a magic exponent.
    const shape = Math.sin(Math.PI * t ** (Math.log(0.5) / Math.log(peak)));
    const centre =
      -bow * shape +
      slope * t +
      Math.sin(slowPhase + t * slowFreq * Math.PI) * slowAmp +
      Math.sin(fastPhase + t * fastFreq) * fastAmp;
    const half = taper(t ** lift, weight);
    lowest = Math.min(lowest, centre - half);
    highest = Math.max(highest, centre + half);
    top.push(`${round(x)} ${round(centre - half)}`);
    bottom.push(`${round(x)} ${round(centre + half)}`);
  }
  bottom.reverse();

  const d = `M${top.join(" L")} L${bottom.join(" L")} Z`;
  // y = 0 is the baseline the caller placed the line along; the ink lives
  // above and below it, and the box is what was actually drawn.
  const pad = 1;
  const box = {
    x: 0,
    y: round(lowest - pad),
    width: round(width),
    height: round(highest - lowest + pad * 2),
  };
  return { d, viewBox: `${box.x} ${box.y} ${box.width} ${box.height}`, ...box };
}

/**
 * The long vertical rule between one client and the next. Same ink, drawn
 * downward — so it wanders sideways rather than up and down, and because it is
 * long it tapers only near its very ends.
 */
export function rulePath(
  height: number,
  seed: string,
  { thickness = 7, samples = 44 }: { thickness?: number; samples?: number } = {},
): Mark {
  const rand = random(seed);
  const wander = thickness * (0.7 + rand() * 0.8);
  const phase = rand() * Math.PI * 2;
  const from = height * 0.015;
  const span = height * 0.97 - from;

  const left: string[] = [];
  const right: string[] = [];
  for (let i = 0; i <= samples; i += 1) {
    const t = i / samples;
    const y = from + span * t;
    const centre = Math.sin(phase + t * 3.1) * wander + Math.sin(phase * 2 + t * 8.7) * wander * 0.25;
    // A long line holds its width for most of its run and only thins at the tips.
    const half = (thickness / 2) * Math.min(1, Math.sin(Math.PI * t) * 4) ** 0.5;
    left.push(`${round(centre - half)} ${round(y)}`);
    right.push(`${round(centre + half)} ${round(y)}`);
  }
  right.reverse();

  const d = `M${left.join(" L")} L${right.join(" L")} Z`;
  const reach = round(wander * 1.3 + thickness);
  const box = { x: -reach, y: 0, width: reach * 2, height: round(height) };
  return { d, viewBox: `${box.x} ${box.y} ${box.width} ${box.height}`, ...box };
}

/**
 * A back-arrow, drawn on the board rather than printed on a chrome button.
 *
 * Three strokes in one path — the shaft and the two barbs of the head — each
 * tapered the way the separators and rules are, and each pulled off true by the
 * same seeded wobble, so it reads as marker rather than as an icon. It is the
 * one control that sits *on* the whiteboard instead of in the frame around it,
 * and a button with a border and a drop shadow was the only thing up there
 * pretending to be software.
 */
export function arrowPath(
  seed: string,
  { length = 58, thickness = 7, head = 22 }: { length?: number; thickness?: number; head?: number } = {},
): Mark {
  const rand = random(seed);
  const samples = 20;

  // Points left. The tip is the origin the head is built around.
  const tipX = 0;
  const tipY = 0;
  const tailX = length * (0.94 + rand() * 0.1);
  const bow = thickness * (0.5 + rand() * 1.1) * (rand() < 0.5 ? -1 : 1);
  const tailDrift = thickness * (rand() - 0.5) * 1.2;

  const parts: string[] = [];
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  /** One tapered stroke from a to b, bowed sideways by `lift`. */
  const stroke = (
    ax: number, ay: number, bx: number, by: number, lift: number, weight: number,
  ) => {
    const dx = bx - ax;
    const dy = by - ay;
    const span = Math.hypot(dx, dy) || 1;
    // Unit normal, so the bow pushes across the stroke rather than along it.
    const nx = -dy / span;
    const ny = dx / span;
    const top: string[] = [];
    const bottom: string[] = [];
    for (let i = 0; i <= samples; i += 1) {
      const t = i / samples;
      const across = Math.sin(Math.PI * t) * lift;
      const cx = ax + dx * t + nx * across;
      const cy = ay + dy * t + ny * across;
      const half = taper(t, weight);
      top.push(`${round(cx + nx * half)} ${round(cy + ny * half)}`);
      bottom.push(`${round(cx - nx * half)} ${round(cy - ny * half)}`);
      minX = Math.min(minX, cx - weight);
      maxX = Math.max(maxX, cx + weight);
      minY = Math.min(minY, cy - weight);
      maxY = Math.max(maxY, cy + weight);
    }
    bottom.reverse();
    parts.push(`M${top.join(" L")} L${bottom.join(" L")} Z`);
  };

  stroke(tailX, tailDrift, tipX, tipY, bow, thickness);
  // The barbs are drawn from the tip outward, which is the direction a hand
  // moves, and they are not the same length as each other.
  stroke(tipX, tipY, head * (0.85 + rand() * 0.3), -head * (0.62 + rand() * 0.2),
    thickness * 0.5 * (rand() - 0.5), thickness * 0.9);
  stroke(tipX, tipY, head * (0.85 + rand() * 0.3), head * (0.62 + rand() * 0.2),
    thickness * 0.5 * (rand() - 0.5), thickness * 0.9);

  const pad = 1.5;
  const box = {
    x: round(minX - pad),
    y: round(minY - pad),
    width: round(maxX - minX + pad * 2),
    height: round(maxY - minY + pad * 2),
  };
  return { d: parts.join(" "), viewBox: `${box.x} ${box.y} ${box.width} ${box.height}`, ...box };
}

/** Small deterministic angle for a sticky, so a note keeps its tilt. */
export function tiltOf(id: string): number {
  // −1.6° … +1.6°, in 0.4° steps. Enough to read as hand-placed, not as broken.
  return ((hashString(id) % 9) - 4) * 0.4;
}
